import { toast } from "sonner";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/services/api/apiRequest";
import type {
  AdminApplication,
  ApplicationPayload,
  PublicConfig,
} from "../applications.types";

export function usePublicConfig() {
  return useQuery<PublicConfig>({
    queryKey: ["afell", "config"],
    queryFn: () => apiRequest("/afell/config/"),
  });
}

export function useSubmitApplication() {
  return useMutation<{ id: number }, Error, ApplicationPayload>({
    mutationFn: (payload) => apiRequest("/afell/applications/", "POST", payload),
  });
}

/** Organizations with a paying member, for the fee-waiver selector.
 *
 * Signed-in rather than public: applying needs an account now, so there is no
 * reason to publish which organizations hold paid memberships. Names and ids
 * only, with the member's own marked — those are the only ones they can claim
 * through, since a claim is about an organization you steward. */
export function useFundedOrganizations() {
  return useQuery<{
    organizations: { id: number; name: string; yours: boolean }[];
  }>({
    queryKey: ["afell", "organizations", "funded"],
    queryFn: () => apiRequest("/afell/organizations/funded/"),
  });
}

export function usePendingApplications() {
  return useQuery<{ applications: AdminApplication[] }>({
    queryKey: ["afell", "applications", "pending"],
    queryFn: () => apiRequest("/afell/admin/applications/pending/"),
  });
}

export function useReviewedApplications() {
  return useQuery<{ applications: AdminApplication[] }>({
    queryKey: ["afell", "applications", "reviewed"],
    queryFn: () => apiRequest("/afell/admin/applications/reviewed/"),
  });
}

/**
 * A decline carries the note the applicant is emailed. An approval carries
 * `waive` when the reviewer answered the fee-waiver question — true or false,
 * with undefined meaning "as the application asked".
 */
type ReviewInput = number | { id: number; note?: string; waive?: boolean };

function useReviewMutation(action: "approve" | "decline") {
  const queryClient = useQueryClient();
  return useMutation<AdminApplication, Error, ReviewInput>({
    mutationFn: (input) => {
      const { id, note, waive } =
        typeof input === "number" ? { id: input, note: "", waive: undefined } : input;
      return apiRequest(`/afell/admin/applications/${id}/${action}/`, "POST", {
        note: note ?? "",
        ...(waive === undefined ? {} : { waive }),
      });
    },
    onSuccess: (application) => {
      queryClient.invalidateQueries({ queryKey: ["afell", "applications"] });
      // The members page is where this is done from, and approving changes
      // the row's tier, its listing and its payment state.
      queryClient.invalidateQueries({ queryKey: ["afell", "admin", "members"] });
      // Say it happened. The row re-renders either way, but on a queue of 470
      // the difference between "approved" and "my click missed" was the row
      // quietly moving. Asked for after the 1 September demo.
      const who = application?.name || "The application";
      if (action === "approve") {
        toast.success(`${who} is in the directory`);
      } else {
        toast(`${who} was declined`);
      }
    },
  });
}

export const useApproveApplication = () => useReviewMutation("approve");
export const useDeclineApplication = () => useReviewMutation("decline");
